import { Grid, Link, Typography } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { defaultPeerGroupComparisonsAppState } from "../../common/reducers/appState";
import { appHeaderTitle } from "../../common/services/Utilities";
import BackButton from "../../containers/BackButton";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import FinancialReportingSearchForm from "../../containers/FinancialReportingSearchForm";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { KeyValue, Query, SortDirection } from "../../interfaces";
import { AppBody } from "../AppBody";
import HelpButton from "../HelpContent/HelpButton";
import CustomizedTable from "./CustomizedTable";

interface Props {
  appState: Query;
  currentApp: string;
  userId: string;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateUi: (
    uiField: string,
    uiValue: KeyValue[] | string[] | boolean | string | undefined | KeyValue,
  ) => void;
  resetState: (appState: Query) => void;
}
export default function CustomizedSearch({
  appState,
  currentApp,
  userId,
  updateSort,
  updateUi,
  resetState,
}: Props) {
  const introParagraph = (
    <>
      {appHeaderTitle("customizedPeerGroup")}
      <Typography variant="h2">Customized Comparison Reports</Typography>
      <Typography
        variant="body2"
        color="primary"
        style={{ paddingRight: "10px" }}
      >
        Compare the same financial data between individual institutions, peer
        groups, or bank holding companies in any combination. You can also
        choose what data you wish to see compared between the institutions or
        peer groups you’ve selected. If you need assistance, please see the{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>Help Section</RouterLink>{" "}
        for tutorials to get you started.
      </Typography>
      <Typography variant="body2" color="primary" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupCustomized"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  // Search form resets when formKey is updated
  const [formKey, setFormKey] = useState(0);

  const resetSearchForm = () => {
    const uiState = {
      ...defaultPeerGroupComparisonsAppState.ui,
      savedPGCSearch: appState.ui.savedPGCSearch,
      customPeerGroups: appState.ui.customPeerGroups,
      standardPeerGroups: appState.ui.standardPeerGroups,
      standardGroupSearchPills: appState.ui.standardGroupSearchPills,
    };
    const newState = { ...defaultPeerGroupComparisonsAppState, ui: uiState };
    setFormKey(prevKey => prevKey + 1);
    resetState(newState);
  };

  useInitialPageLoad("peerGroup");
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Peer Group Comparisons" />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        introText={introParagraph}
        appState={appState}
        backButton={
          <BackButton
            app="FinancialReporting"
            buttonLabel="Back to Financial Reporting"
            variant="dark"
          />
        }
        searchForm={
          <FinancialReportingSearchForm
            initialSubmit={!appState.ui.searchPush}
            currentApp={currentApp}
            includeMultiCert={true}
            pgcApp="Customized Comparisons"
            noHeader
            displayReset={true}
            resetForm={resetSearchForm}
            key={formKey}
          />
        }
      >
        <Typography variant="h2">Report Population</Typography>
        <hr />
        <CustomizedTable
          appState={appState}
          updateSort={updateSort}
          updateUi={updateUi}
          resetState={resetState}
          userId={userId}
        />
      </AppBody>
    </>
  );
}
